import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { settingsService } from '../services/settingsService';
import Modal from '../../../components/common/Modal';
import { XMarkIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const SettingModal = ({ isOpen, onClose, onSuccess, setting }) => {
  const [formData, setFormData] = useState({
    key: '',
    value: '',
  });
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (setting) {
      setFormData({
        key: setting.key,
        value: setting.value || '',
      });
      setIsEditing(true);
    } else {
      setFormData({
        key: '',
        value: '',
      });
      setIsEditing(false);
    }
    setErrors({});
  }, [setting, isOpen]);

  const createMutation = useMutation({
    mutationFn: (data) => settingsService.createSetting(data),
    onSuccess: () => {
      toast.success('Paramètre créé avec succès');
      onSuccess();
    },
    onError: (error) => {
      toast.error('Erreur lors de la création du paramètre');
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }) => settingsService.updateSetting(key, value),
    onSuccess: () => {
      toast.success('Paramètre modifié avec succès');
      onSuccess();
    },
    onError: (error) => {
      toast.error('Erreur lors de la modification du paramètre');
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.key.trim()) {
      newErrors.key = 'La clé est obligatoire';
    }

    if (!formData.value.trim()) {
      newErrors.value = 'La valeur est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (isEditing) {
      updateMutation.mutate({ key: setting.key, value: formData.value });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Modifier le paramètre' : 'Ajouter un paramètre'}
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
            onClick={onClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="key" className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Clé du paramètre *
            </label>
            <input
              type="text"
              id="key"
              name="key"
              value={formData.key}
              onChange={handleInputChange}
              disabled={isEditing}
              className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                errors.key ? 'border-red-300 bg-red-50' : 'border-gray-300'
              } ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Ex: about_us, privacy_policy, contact_info..."
            />
            {errors.key && <p className="mt-1 text-sm text-red-600 text-left">{errors.key}</p>}
          </div>

          <div>
            <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Valeur du paramètre *
            </label>
            <textarea
              id="value"
              name="value"
              rows={8}
              value={formData.value}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-vertical ${
                errors.value ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Saisissez la valeur du paramètre..."
            />
            {errors.value && <p className="mt-1 text-sm text-red-600 text-left">{errors.value}</p>}
            <p className="mt-1 text-xs text-gray-500 text-left">
              Vous pouvez saisir du texte simple, du HTML ou du JSON selon vos besoins.
            </p>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-3 text-sm font-medium text-white bg-primary border border-transparent rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'En cours...' : (isEditing ? 'Mettre à jour' : 'Ajouter')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SettingModal;
